import React from 'react';
import { Card } from './ui/card';
import { MapPin, Phone, Clock, Heart, Target, Users, Mail } from 'lucide-react';
import { OrderButton } from './OrderButton';

export const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Helpful Attitude",
      description: "We're genuinely committed to helping small businesses succeed and grow their brand presence."
    },
    {
      icon: Target,
      title: "Quality First",
      description: "Every product meets our high standards for durability, style, and professional appearance."
    },
    {
      icon: Users,
      title: "No Minimums",
      description: "Whether you need 1 item or 1000, we're here to serve businesses of all sizes."
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            About <span className="text-blue-600">Avid Branding</span>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            A division of DA-12 Corp, proudly serving Orangeville and surrounding areas 
            with premium custom apparel and branding solutions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left Content */}
          <div>
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-700 text-lg leading-relaxed">
                  We believe every small business deserves to have a professional brand presence 
                  without breaking the bank. That's why we offer high-quality custom printed and 
                  embroidered apparel with no minimum purchase requirements.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">What Sets Us Apart</h3>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Don't have a logo yet? No problem! We'll help craft the vision for your brand 
                  during a consultation. Our goal is to help small businesses get started with 
                  putting their brand out there, and we'll even create a free sample so you can 
                  experience our quality firsthand.
                </p>
              </div>

              <div className="bg-blue-100 p-6 rounded-xl">
                <p className="text-blue-800 font-semibold text-lg">
                  "Our apparel is durable, stylish, and designed for performance - whether you need 
                  warm materials for outdoor work or moisture-wicking fabrics for active lifestyles."
                </p>
              </div>

              <OrderButton
                label="Start Your Order"
                testid="about-start-order-button"
                className="px-10 py-5 text-lg"
              />
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/k1hi16fc_1000031721.jpg" 
              alt="Avidbranding.ca professional business location at Dufferin Board of Trade"
              className="w-full aspect-square object-cover rounded-2xl shadow-lg"
            />
            <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-gray-700">Professional Location</span>
              </div>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {values.map((value, index) => (
            <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow duration-200 border-2 border-blue-100">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <value.icon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
              <p className="text-gray-600">{value.description}</p>
            </Card>
          ))}
        </div>

        {/* Location & Hours */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="p-6 text-center bg-white border-2 border-blue-100">
            <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Our Location</h3>
            <p className="text-gray-600">4-246372 Hockley Rd</p>
            <p className="text-gray-600">Orangeville, ON</p>
          </Card>

          <Card className="p-6 text-center bg-white border-2 border-sky-100">
            <div className="bg-sky-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="h-6 w-6 text-sky-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Us</h3>
            <p className="text-gray-600 font-semibold">communications@da-12.com</p>
            <p className="text-gray-600">24hr response time</p>
          </Card>

          <Card className="p-6 text-center bg-white border-2 border-blue-100">
            <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Call Us</h3>
            <p className="text-gray-600 font-semibold">519-938-8959</p>
          </Card>

          <Card className="p-6 text-center bg-white border-2 border-blue-100">
            <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-6 w-6 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Office Hours</h3>
            <p className="text-gray-600">Monday - Saturday</p>
            <p className="text-gray-600">By Appointment</p>
          </Card>
        </div>
      </div>
    </section>
  );
};